import Decimal from 'decimal.js';
import { compact } from 'lodash';

import { FUNCTIONAL } from '@/styles/tokens/colors';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';

import { EstateWaterfall_TransferLogItemFragment } from '../../../graphql/EstateWaterfall.generated';
import { EdgeLabelCard } from '../edgeLabelCard.component';
import { CardConfig } from './types';
import { getTrustTermDisplay } from './utils';

interface CrtIncomeCardParams {
  sourceEntityName: string;
  targetEntityName: string;
  incomePayments?: EstateWaterfall_TransferLogItemFragment[];
  crtFunding?: EstateWaterfall_TransferLogItemFragment;
}

export function createCrtIncomeCard({
  sourceEntityName,
  targetEntityName,
  incomePayments,
  crtFunding,
}: CrtIncomeCardParams): CardConfig {
  const crtName = sourceEntityName;
  const incomeBeneficiary = targetEntityName;

  const isHypothetical = incomePayments?.[0]?.isHypothetical;

  const fundingAmount = (() => {
    if (crtFunding) {
      return crtFunding.finalTransferAmount;
    }

    return null;
  })();

  const term = (() => {
    if (
      incomePayments?.[0]?.associatedObject?.__typename === 'HypotheticalCRT'
    ) {
      const termYears =
        incomePayments[0].associatedObject.simulatedCrt.crtProposal?.termYears;
      return termYears ? `${termYears} years` : null;
    }

    if (
      incomePayments?.[0]?.associatedObject?.__typename === 'Entity' &&
      incomePayments[0].associatedObject.subtype?.__typename === 'CRTTrust'
    ) {
      const { termDurationLifetime, termDurationYears } =
        incomePayments[0].associatedObject.subtype;
      return getTrustTermDisplay({ termDurationLifetime, termDurationYears });
    }

    return null;
  })();

  const termEndYear = (() => {
    if (
      incomePayments?.[0]?.associatedObject?.__typename === 'HypotheticalCRT'
    ) {
      const termYears =
        incomePayments[0].associatedObject.simulatedCrt.crtProposal?.termYears;
      return termYears ? new Date().getFullYear() + termYears : null;
    }

    if (
      incomePayments?.[0]?.associatedObject?.__typename === 'Entity' &&
      incomePayments[0].associatedObject.subtype?.__typename === 'CRTTrust'
    ) {
      const { termDurationLifetime } =
        incomePayments[0].associatedObject.subtype;
      if (termDurationLifetime) {
        return null;
      }
      return incomePayments[0].associatedObject.subtype.termEndDate?.getFullYear();
    }

    return null;
  })();

  // Calculate total payments
  const totalPayments =
    incomePayments?.reduce((sum, payment) => {
      return sum.plus(payment.finalTransferAmount || 0);
    }, new Decimal(0)) || new Decimal(0);

  return {
    element: (
      <EdgeLabelCard
        key={`crt-income-${sourceEntityName}-${targetEntityName}`}
        title={
          isHypothetical
            ? 'Hypothetical CRT income payments'
            : 'CRT income payments'
        }
        titleSx={
          isHypothetical ? { color: FUNCTIONAL.WARNING[600] } : undefined
        }
        description={`${incomeBeneficiary} receives income payments from ${crtName} throughout CRT term.`}
        rows={compact([
          fundingAmount
            ? {
                label: 'Funding amount',
                value: formatCurrencyNoDecimals(fundingAmount),
              }
            : null,
          {
            label: 'Total income payments',
            value: formatCurrencyNoDecimals(totalPayments),
          },
          term
            ? {
                label: 'CRT term',
                value: term,
              }
            : null,
          termEndYear
            ? {
                label: 'Term end',
                value: termEndYear.toString(),
              }
            : null,
        ])}
      />
    ),
    label: 'CRT income',
    value: formatCurrencyNoDecimals(totalPayments),
    variant: isHypothetical ? 'secondary' : 'primary',
  };
}
